import { useMemo, useState } from "react";
import { Modal, Table } from "react-bootstrap";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { PAGE_SIZE_DEFAULT } from "../../../constants";
import {
  deactivateCardToken,
  getCardTokens,
  getCardDetailByUserId,
  manuallyActivate,
} from "../../../services/userServices";
import ErrorPage from "../../ErrorPage";
import PageLoader from "../../PageLoader";
import PaginationComponent from "../../Pagination";
import CardTokenActionModal, { CardTokenAction } from "../CardTokenActionModal";
import styles from "./index.module.scss";
import {
  CardToken,
  CardTokenResponse,
  CardTokenStatus,
  QueryKeyEnum,
} from "../../../types";
import { CardDetail, CardStatus } from "../../../types/CardDetail";

const sizeOfPage = PAGE_SIZE_DEFAULT;

interface CardTokensTableProps {
  isActive: boolean;
}

const AVAILABLE_ACTIONS: Record<CardTokenStatus, CardTokenAction[]> = {
  [CardTokenStatus.Active]: ["deactivate"],
  [CardTokenStatus.Inactive]: ["activate", "deactivate"],
  [CardTokenStatus.Suspended]: ["deactivate"],
  [CardTokenStatus.Deactivated]: [],
};

const ACTION_LABEL: Record<CardTokenAction, string> = {
  activate: "Activate",
  deactivate: "Deactivate",
};

const KNOWN_CARD_TOKEN_STATUSES: string[] = Object.values(CardTokenStatus);

type PendingAction = {
  cardToken: CardToken;
  action: CardTokenAction;
};

export default function CardTokensTable({ isActive }: CardTokensTableProps) {
  const { userId } = useParams();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [pending, setPending] = useState<PendingAction | null>(null);

  const cardTokensQueryKey = [QueryKeyEnum.GetCardTokens, userId];

  const { isLoading, error, data, refetch } = useQuery<
    CardTokenResponse,
    Error
  >(
    cardTokensQueryKey,
    async () => {
      const response = await getCardTokens(userId || "");
      response.tokens.forEach((cardToken) => {
        if (!KNOWN_CARD_TOKEN_STATUSES.includes(cardToken.status)) {
          console.warn(
            `Unrecognized card token status "${cardToken.status}" for token ${cardToken.token} (userId: ${userId}). This token's Activate/Deactivate action will not be shown.`
          );
        }
      });
      return response;
    },
    { enabled: isActive }
  );
  const { data: userCardData } = useQuery<CardDetail, Error>(
    [QueryKeyEnum.GetCardDetail, userId],
    () => getCardDetailByUserId(userId!), // On this page, the userId always exists
    { enabled: isActive }
  );

  useMemo(() => {
    if (isActive) {
      refetch();
    }
  }, [isActive, refetch]);

  const paginatedTokens = useMemo(() => {
    if (!!data?.tokens.length) {
      return data.tokens.slice(
        (currentPage - 1) * sizeOfPage,
        currentPage * sizeOfPage
      );
    }
    return [];
  }, [currentPage, data?.tokens]);

  const setTokenStatus = (token: string, status: CardTokenStatus) => {
    queryClient.setQueryData<CardTokenResponse | undefined>(
      cardTokensQueryKey,
      (previous) =>
        previous && {
          tokens: previous.tokens.map((t) =>
            t.token === token ? { ...t, status } : t
          ),
        }
    );
  };

  type ActionVariables = {
    token: string;
    reason: string;
    action: CardTokenAction;
  };

  const actionMutation = useMutation({
    mutationFn: ({ token, reason, action }: ActionVariables) =>
      action === "activate"
        ? manuallyActivate(userId ?? "", token, reason)
        : deactivateCardToken(userId ?? "", token, reason),
    onSuccess: (_, { token, action }) => {
      setTokenStatus(
        token,
        action === "activate"
          ? CardTokenStatus.Active
          : CardTokenStatus.Deactivated
      );
      setPending(null);
      toast.success(
        action === "activate"
          ? "Card token activated."
          : "Card token deactivated."
      );
    },
    onError: (mutationError: Error) => {
      toast.error(
        `An error happened. Please reach out to the engineering team with the following data: error: ${mutationError.message}, userId: ${userId}`
      );
    },
  });

  const handleConfirmAction = (reason: string) => {
    if (pending) {
      actionMutation.mutate({
        token: pending.cardToken.token,
        reason,
        action: pending.action,
      });
    }
  };

  if (!isActive) {
    return <></>;
  }

  if (isLoading)
    return <PageLoader isLoading={isLoading} content="Loading card tokens.." />;

  if (error || !data) return <ErrorPage />;

  const isCardPermBlocked = userCardData?.status === CardStatus.PermBlock;

  return (
    <div>
      <div className={styles.boxExternalPagination}>
        <span className={styles.listGroupTitle}>Card Tokens</span>
        <PaginationComponent
          pageCount={Math.ceil(data.tokens.length / sizeOfPage)}
          currentPage={currentPage}
          itemOfPage={paginatedTokens.length}
          totalItems={data.tokens.length}
          setCurrentPage={setCurrentPage}
        />
      </div>
      <div className={styles.table}>
        <Table hover bordered responsive="md">
          <thead>
            <tr>
              <th>Digital card location</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedTokens.length > 0 ? (
              paginatedTokens.map((cardToken) => {
                const actions = AVAILABLE_ACTIONS[cardToken.status] ?? [];
                return (
                  <tr key={cardToken.token}>
                    <td>{cardToken.name}</td>
                    <td>{cardToken.status}</td>
                    <td>
                      {actions.length > 0
                        ? actions.map((action, index) => (
                            <span key={action}>
                              {index > 0 && " | "}
                              <b
                                className={styles.tokenAction}
                                onClick={() =>
                                  !isCardPermBlocked &&
                                  setPending({ cardToken, action })
                                }
                                data-disable={isCardPermBlocked}
                              >
                                {ACTION_LABEL[action]}
                              </b>
                            </span>
                          ))
                        : "-"}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={3} className={styles.emptyTable}>
                  Oops, no results were found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <Modal
        centered
        show={!!pending}
        onHide={() => setPending(null)}
        backdrop={actionMutation.isLoading ? "static" : true}
        keyboard={!actionMutation.isLoading}
      >
        {pending && (
          <CardTokenActionModal
            action={pending.action}
            isSubmitting={actionMutation.isLoading}
            onConfirm={handleConfirmAction}
            onCancel={() => setPending(null)}
          />
        )}
      </Modal>
    </div>
  );
}
